// src/components/common/Subscription/SubscriptionExpiredModal.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Calendar, ArrowRight } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useSubscriptionStatus } from '../../../hooks/useSubscriptionStatus';
import { usePermissions } from '../../../hooks/usePermissions';
import styles from './SubscriptionExpiredModal.module.css';

const SubscriptionExpiredModal = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { subscription, isExpired, isCancelled, isSuspended } = useSubscriptionStatus();
  const { hasPermission } = usePermissions();

  const canViewSubscription = hasPermission('view_subscription');
  const shouldShow = isExpired || isCancelled || isSuspended;

  if (!shouldShow) {
    return null;
  }

  const handleRenewClick = () => {
    if (canViewSubscription) {
      navigate('/subscription/plans');
    }
  };

  const getTitle = () => {
    if (isExpired) return t('subscription.subscriptionExpired');
    if (isCancelled) return t('subscription.subscriptionCancelled');
    if (isSuspended) return t('subscription.subscriptionSuspended');
    return t('subscription.subscriptionExpired');
  };

  const getMessage = () => {
    if (isExpired) {
      return t('subscription.subscriptionExpiredMessage');
    }
    if (isCancelled) {
      return t('subscription.subscriptionCancelledMessage');
    }
    if (isSuspended) {
      return t('subscription.subscriptionSuspendedMessage');
    }
    return t('subscription.renewToAccessFeatures');
  };

  const getExpiredDate = () => {
    if (!subscription) return null;
    
    if (subscription.endDate) {
      return new Date(subscription.endDate).toLocaleDateString();
    }
    if (subscription.nextBillingDate) {
      return new Date(subscription.nextBillingDate).toLocaleDateString();
    }
    return null;
  };

  const expiredDate = getExpiredDate();

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.iconWrapper}>
          <div className={styles.icon}>
            <AlertCircle size={64} />
          </div>
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>{getTitle()}</h1>
          <p className={styles.message}>{getMessage()}</p>

          {expiredDate && (
            <div className={styles.expiredInfo}>
              <Calendar size={16} />
              <span>
                {t('subscription.expiredOn')}: <strong>{expiredDate}</strong>
              </span>
            </div>
          )}

          {subscription && (
            <div className={styles.planInfo}>
              <div className={styles.planLabel}>{t('subscription.previousPlan')}</div>
              <div className={styles.planValue}>{subscription.planName}</div>
            </div>
          )}
        </div>

        {canViewSubscription && (
          <div className={styles.actions}>
            <button
              className={styles.renewButton}
              onClick={handleRenewClick}
            >
              {t('subscription.renewSubscription')}
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {!canViewSubscription && (
          <div className={styles.noPermission}>
            <p>{t('subscription.contactAdminToRenew')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionExpiredModal;